/*
  card-video.js — plays the product card video (custom.card_video metafield)
  only when the card is on screen. If the video can't play for any reason
  (load error, autoplay blocked, Low Power Mode, reduced motion), the element
  is removed and the product image underneath shows instead.
  Reasons are logged to the console as "[card-video] ..." for debugging.
*/
class CardVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    // Respect "reduce motion": keep the still image
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.fail('reduced motion is on');

    const source = this.video.querySelector('source');
    if (source) source.addEventListener('error', () => this.fail('video failed to load'));
    this.video.addEventListener('error', () => this.fail('video error'));
    this.video.addEventListener('playing', () => this.classList.add('is-playing'));

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const attempt = this.video.play();
          if (attempt && attempt.catch) {
            attempt.catch((e) => {
              // AbortError just means a pause() interrupted play() while scrolling — not a real failure
              if (e.name !== 'AbortError') this.fail('play() blocked: ' + e.name);
            });
          }
        } else {
          this.video.pause();
        }
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  fail(reason) {
    const source = this.querySelector('source');
    console.warn('[card-video]', reason || 'unknown', source ? source.src : '');
    if (this.observer) this.observer.disconnect();
    this.remove();
  }
}

if (!customElements.get('card-video')) customElements.define('card-video', CardVideo);
